import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import FusionMap from '../components/FusionMap';
import { 
  ArrowLeftIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
  MapIcon,
  ShareIcon,
  EyeIcon,
  EyeSlashIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

// ADDED: Import enhanced health calculation functions
import { calculateEnhancedSessionHealthScore } from '../utils/sessionHealth';

function SessionPage() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [segments, setSegments] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [statsVisible, setStatsVisible] = useState(true);
  const [payTable,  setPayTable]  = useState(null);        
  const [project, setProject] = useState(null);
  const [projectConfig, setProjectConfig] = useState(null);
  const [payAnalysisAvailable, setPayAnalysisAvailable] = useState(false);
  
  // ADDED: Enhanced health data state
  const [enhancedHealthData, setEnhancedHealthData] = useState(null);
  

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId]);

  // ADDED: Calculate enhanced health data when segments/events/config change
  useEffect(() => {
    if (segments.length > 0 && projectConfig) {
      const context = {
        iriThreshold: projectConfig.iri_threshold_type_a || 160,
        useNonLinear: true,
        measurementQuality: { variability: 0.1 }
      };
      
      try {
        const healthResults = calculateEnhancedSessionHealthScore(segments, events, context);
        setEnhancedHealthData(healthResults);
      } catch (error) {
        console.error('Error calculating enhanced health data:', error);
        setEnhancedHealthData(null);
      }
    } else {
      setEnhancedHealthData(null);
    }
  }, [segments, events, projectConfig]);

  const navigate = useNavigate();

  const handleBackClick = () => {
    // Go back to the previous page in browser history
    navigate(-1);
  };

  const handleBackToProjects = () => {
    // Navigate to the projects page
    navigate('/projects');
  };

  // Replace the fetchSessionData function in SessionPage.js
const fetchSessionData = async () => {
  try {
    setLoading(true); setError(null);

    /* 1. session row */
    const { data: ses, error: sesErr } = await supabase
      .from('prototype_sessions')
      .select('*')
      .eq('session_id', sessionId)
      .single();
    if (sesErr) throw new Error(sesErr.message);
    setSession(ses);

    /* 2. project with configuration and pay table */
    const { data: projectData, error: projErr } = await supabase
      .from('projects')
      .select(`
        *,
        project_configurations(*),
        pay_tables(id, owner_name, json_table)
      `)
      .eq('id', ses.project_id)
      .single();
    if (projErr) throw new Error(projErr.message);
    
    setProject(projectData);
    
    // FIXED: Handle different possible structures for project_configurations
    let config = null;
    
    if (Array.isArray(projectData.project_configurations)) {
      // Standard array structure
      config = projectData.project_configurations[0] || null;
    } else if (projectData.project_configurations && typeof projectData.project_configurations === 'object') {
      // Single object structure (not array)
      config = projectData.project_configurations;
    } else {
      // Check if config data is embedded directly in project
      const configFields = [
        'lane_width_ft', 'unit_price_per_ton', 'spread_rate_lbs_sy', 
        'is_configured', 'configured_at'
      ];
      
      const hasConfigFields = configFields.some(field => 
        projectData.hasOwnProperty(field) && projectData[field] !== undefined
      );
      
      if (hasConfigFields) {
        // Extract config fields from project object
        config = {
          id: projectData.config_id || null,
          project_id: projectData.id,
          lane_width_ft: projectData.lane_width_ft,
          unit_price_per_ton: projectData.unit_price_per_ton,
          spread_rate_lbs_sy: projectData.spread_rate_lbs_sy,
          lot_length_ft: projectData.lot_length_ft,
          profile_start_station: projectData.profile_start_station,
          profile_end_station: projectData.profile_end_station,
          base_2_qty: projectData.base_2_qty,
          og_layer_qty: projectData.og_layer_qty,
          base_1_qty: projectData.base_1_qty,
          intermed_qty: projectData.intermed_qty,
          surface_qty: projectData.surface_qty,
          analysis_segment_length_ft: projectData.analysis_segment_length_ft,
          iri_threshold_type_a: projectData.iri_threshold_type_a,
          iri_threshold_type_b: projectData.iri_threshold_type_b,
          pavement_type: projectData.pavement_type,
          material_density_lbs_cy: projectData.material_density_lbs_cy,
          contract_total_value: projectData.contract_total_value,
          is_configured: projectData.is_configured,
          configured_by: projectData.configured_by,
          configured_at: projectData.configured_at,
          created_at: projectData.config_created_at || projectData.created_at,
          updated_at: projectData.config_updated_at || projectData.updated_at
        };
      }
    }
    
    setProjectConfig(config);

    /* 3. segments + events (existing code) */
    const { data: segs } = await supabase
      .from('prototype_segments')
      .select('*')
      .eq('session_id', sessionId)
      .order('from_station_ft');
    setSegments(segs || []);

    const { data: evs } = await supabase
      .from('prototype_events')
      .select('*')
      .eq('session_id', sessionId)
      .order('from_station_ft');
    setEvents(evs || []);

    // Check if pay analysis is available
    const hasConfig = config?.is_configured;
    const hasPayTable = projectData.pay_table_id && projectData.pay_tables;
    const hasSegments = (segs?.length > 0);
    
    setPayAnalysisAvailable(hasConfig && hasPayTable && hasSegments);

    /* 5. bounds (unchanged) */
    const { data: b } = await supabase.rpc(
      'get_prototype_session_bounds', { session_id_input: sessionId }
    );
    if (b?.length) setMapBounds(b[0]);

  } catch (err) {
    console.error(err);
    setError(err.message || 'Failed to load session');
  } finally { setLoading(false); }
};

  const isProcessing = session?.status === 'PROCESSING' || session?.status === 'PENDING_RAW';

  // Calculate basic statistics for the stats bar
  const totalSegments = segments.length;
  const totalEvents = events.length;
  const bumpCount = events.filter(e => e.event_type === 'Bump').length;
  const dipCount = events.filter(e => e.event_type === 'Dip').length;
  const lrCount = events.filter(e => e.event_type === 'LR').length;
  
  const totalDistance = segments.reduce((sum, segment) => {
    return sum + (segment.distance_ft || 0);
  }, 0);
  
  const validIRISegments = segments.filter(s => s.iri_avg_in_per_mi && s.iri_avg_in_per_mi > 0);
  const avgIRI = validIRISegments.length > 0 
    ? validIRISegments.reduce((sum, s) => sum + s.iri_avg_in_per_mi, 0) / validIRISegments.length
    : 0;

  const startStation = session?.start_station_ft || 
    (segments.length > 0 ? Math.min(...segments.map(s => s.from_station_ft || 0)) : 0);
  const endStation = session?.end_station_ft || 
    (segments.length > 0 ? Math.max(...segments.map(s => s.to_station_ft || 0)) : 0);

  const formatDistance = (feet) => {
    if (feet >= 5280) {
      return `${(feet / 5280).toFixed(1)} mi`;
    }
    return `${feet.toLocaleString()} ft`;
  };

  const formatStation = (station) => {
    if (!station) return 'N/A';
    const hundreds = Math.floor(station / 100);
    const remainder = station % 100;
    return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
  };

  // UPDATED: Use enhanced health data for condition assessment if available
  const getConditionColor = (iri) => {
    if (enhancedHealthData?.overall_category) {
      // Use enhanced health category colors
      return `text-${enhancedHealthData.overall_category.color.replace('#', '')}-400`;
    }
    
    // Fallback to original IRI-based coloring
    if (iri < 95) return 'text-green-600';
    if (iri < 120) return 'text-lime-600';
    if (iri < 170) return 'text-yellow-600';
    if (iri < 270) return 'text-orange-600';
    return 'text-red-600';
  };

  const getConditionLabel = (iri) => {
    if (enhancedHealthData?.overall_category) {
      // Use enhanced health category
      return enhancedHealthData.overall_category.label;
    }
    
    // Fallback to original IRI-based labeling
    if (iri < 95) return 'Good';
    if (iri < 120) return 'Good-Fair';
    if (iri < 170) return 'Fair';
    if (iri < 270) return 'Poor';
    return 'Very Poor';
  };

  const handlePayAnalysisClick = () => {
    if (!projectConfig?.is_configured) {
      alert('Project configuration is required for pay analysis. Please configure the project first.');
      navigate(`/projects/${project?.id}?tab=configure`);
      return;
    }
    
    if (!project?.pay_table_id) {
      alert('A pay table must be assigned to the project for pay analysis.');
      navigate(`/projects/${project?.id}?tab=overview`);
      return;
    }
    
    navigate(`/session/${sessionId}/pay-analysis`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-300 mt-4">Loading session data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Session Not Found</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <Link 
            to="/"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Upload
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col overflow-hidden">
      {/* Compact Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToProjects}
                className="inline-flex items-center text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </button>
              <div className="h-5 border-l border-gray-600"></div>
              <div>
                <h1 className="text-lg font-bold text-white">
                  {session?.filename || 'Road Profile Analysis'}
                </h1>
                <div className="flex items-center space-x-4 text-xs text-gray-400">
                  <span>ID: {sessionId?.slice(0, 8)}...</span>
                  {session?.run_date && (
                    <span>{new Date(session.run_date).toLocaleDateString()}</span>
                  )}
                  {session?.lane_no && <span>Lane {session.lane_no}</span>}
                  {/* ADDED: Enhanced health indicator */}
                  {enhancedHealthData && (
                    <span className="text-xs px-2 py-1 rounded" style={{ 
                      backgroundColor: enhancedHealthData.overall_category.bgColor,
                      color: enhancedHealthData.overall_category.color 
                    }}>
                      Health: {enhancedHealthData.overall_health_score} ({enhancedHealthData.overall_category.label})
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {isProcessing && (
                <div className="flex items-center text-yellow-400 text-sm">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Processing...
                </div>
              )}
              
              <button
                onClick={() => setStatsVisible(!statsVisible)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title={statsVisible ? "Hide stats" : "Show stats"}
              >
                {statsVisible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
              >
                <ShareIcon className="h-4 w-4 mr-1" />
                Share
              </button>

              <button
                onClick={() => {
                  // Navigate to analysis page (assuming it exists)
                  navigate(`/session/${sessionId}/analysis`);
                
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
              >
                <ChartBarIcon className="h-4 w-4 mr-1" />
                Analysis
              </button>

              <button
                onClick={handlePayAnalysisClick}
                disabled={!payAnalysisAvailable}
                className={`inline-flex items-center px-3 py-1.5 text-sm transition-colors ${
                  payAnalysisAvailable
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 cursor-not-allowed opacity-50'
                }`}
                title={
                  !payAnalysisAvailable
                    ? 'Requires project configuration and pay table'
                    : 'View pay analysis and INDOT calculations'
                }
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                Pay Analysis
                {!projectConfig?.is_configured && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Setup Required
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* UPDATED: Stats Bar with enhanced health data */}
      {statsVisible && (
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex-shrink-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{formatDistance(totalDistance)}</div>
              <div className="text-xs text-gray-400">Total Distance</div>
              <div className="text-xs text-gray-500">{formatStation(startStation)} → {formatStation(endStation)}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{totalSegments}</div>
              <div className="text-xs text-gray-400">Road Segments</div>
              <div className="text-xs text-gray-500">
                {avgIRI > 0 ? `Avg IRI: ${avgIRI.toFixed(1)}` : 'No IRI data'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">{totalEvents}</div>
              <div className="text-xs text-gray-400">Events Detected</div>
              <div className="text-xs text-gray-500">{bumpCount}B · {dipCount}D · {lrCount}R</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getConditionColor(avgIRI)}`}>
                {enhancedHealthData ? enhancedHealthData.overall_health_score : (avgIRI > 0 ? getConditionLabel(avgIRI) : 'N/A')}
              </div>
              <div className="text-xs text-gray-400">
                {enhancedHealthData ? 'Enhanced Health Score' : 'Road Condition'}
              </div>
              <div className="text-xs text-gray-500">
                {enhancedHealthData ? 
                  `Multi-factor: ${enhancedHealthData.avg_smoothness_score}S · ${enhancedHealthData.avg_alr_score}A · ${enhancedHealthData.avg_consistency_score || 'N/A'}C` :
                  (avgIRI > 0 ? `${avgIRI.toFixed(1)} in/mi` : 'No data')
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UPDATED: Main Content - Map with enhanced health data */}
      <div className="flex-1 relative">
        <div className="absolute inset-0">
          {segments.length > 0 || events.length > 0 ? (
            <FusionMap
              sessionId={sessionId}
              segments={segments}
              events={events}
              bounds={mapBounds}
              session={session}
              projectConfig={projectConfig}
              enhancedHealthData={enhancedHealthData}
            />
          ) : (
            <div className="h-full flex items-center justify-center bg-gray-800 text-gray-400">
              <div className="text-center">
                <MapIcon className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-lg">No geographic data available</p>
                <p className="text-sm text-gray-500">Upload a file with coordinate data to visualize the road profile</p>
              </div>
            </div>
          )}
        </div>

        {/* Floating Sidebar removed - now integrated into FusionMap */}
      </div>
    </div>
  );
}

export default SessionPage;